import { isTransientNetworkError } from '#~/api/transient-retry.js';

export type AccountStudioInventoryLoadResult<TStudio> =
  | { readonly kind: 'error'; readonly cause: Error }
  | { readonly kind: 'loaded'; readonly studios: readonly TStudio[] }
  | { readonly kind: 'stale' };

export type AccountStudioInventoryLoaderOptions<TStudio> = {
  readonly attempts: number;
  readonly delay?: (milliseconds: number, signal: AbortSignal) => Promise<void>;
  readonly fetchStudios: (signal: AbortSignal) => Promise<readonly TStudio[]>;
  readonly fetchTimeoutMs: number;
  readonly signal: AbortSignal;
};

class UnknownInventoryLoadError extends Error {
  public constructor(cause: unknown) {
    super('Account Studio inventory failed with an unknown cause.', { cause });
    this.name = 'UnknownInventoryLoadError';
  }
}

class InventoryFetchTimeoutError extends Error {
  public constructor(milliseconds: number) {
    super(`Account Studio inventory timed out after ${milliseconds}ms.`);
    this.name = 'InventoryFetchTimeoutError';
  }
}

class InventoryLoadAbortedError extends Error {
  public constructor() {
    super('Account Studio inventory load was aborted.');
    this.name = 'InventoryLoadAbortedError';
  }
}

const delayRespectingAbort = (milliseconds: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const timer = setTimeout(finish, milliseconds);
    function finish(): void {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    }
    signal.addEventListener('abort', finish, { once: true });
  });

const fetchWithDeadline = <TStudio>(
  fetchStudios: (signal: AbortSignal) => Promise<readonly TStudio[]>,
  milliseconds: number,
  signal: AbortSignal
): Promise<readonly TStudio[]> => {
  if (signal.aborted) {
    return Promise.reject(new InventoryLoadAbortedError());
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  let rejectForAbort: ((cause?: unknown) => void) | undefined;
  const onAbort = (): void => rejectForAbort?.(new InventoryLoadAbortedError());
  const abort = new Promise<never>((_resolve, reject) => {
    rejectForAbort = reject;
    signal.addEventListener('abort', onAbort, { once: true });
  });
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new InventoryFetchTimeoutError(milliseconds)), milliseconds);
  });
  let fetch: Promise<readonly TStudio[]>;
  try {
    fetch = fetchStudios(signal);
  } catch (cause) {
    fetch = Promise.reject(cause);
  }

  return Promise.race([fetch, abort, timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
    signal.removeEventListener('abort', onAbort);
  });
};

/** Own the bounded inventory retry so every success, error, and cancellation settles explicitly. */
export const loadAccountStudioInventory = async <TStudio>({
  attempts,
  delay = delayRespectingAbort,
  fetchStudios,
  fetchTimeoutMs,
  signal
}: AccountStudioInventoryLoaderOptions<TStudio>): Promise<
  AccountStudioInventoryLoadResult<TStudio>
> => {
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    if (signal.aborted) {
      return { kind: 'stale' };
    }

    try {
      const studios = await fetchWithDeadline(fetchStudios, fetchTimeoutMs, signal);

      return signal.aborted ? { kind: 'stale' } : { kind: 'loaded', studios };
    } catch (cause) {
      const error = cause instanceof Error ? cause : new UnknownInventoryLoadError(cause);

      if (signal.aborted) {
        return { kind: 'stale' };
      }

      const retryable =
        error instanceof InventoryFetchTimeoutError || isTransientNetworkError(error);
      if (attempt === attempts || !retryable) {
        return { kind: 'error', cause: error };
      }

      await delay(250 * attempt, signal);
      if (signal.aborted) {
        return { kind: 'stale' };
      }
    }
  }

  return { kind: 'error', cause: new UnknownInventoryLoadError('No attempts configured.') };
};
